import React, { useState, useRef } from 'react';
import { Download, Eye, X, FileSignature } from 'lucide-react';
import axios from 'axios';

const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty ', 'Thirty ', 'Forty ', 'Fifty ', 'Sixty ', 'Seventy ', 'Eighty ', 'Ninety '];
  if ((num = num.toString()).length > 9) return 'overflow';
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return;
  let str = '';
  str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + a[n[2][1]]) + 'Lacs ' : '';
  str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] != 0) ? ((str != '') ? '' : '') + (a[Number(n[5])] || b[n[5][0]] + a[n[5][1]]) : '';
  return str.trim() + ' only';
};

const Employee360OfficialNoteCard = ({ employeeId, profile, pensionReport, loading }) => {
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef(null);

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };
  const handleDownloadPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    const opt = {
      margin: [5, 15, 15, 15], // top, left, bottom, right
      filename: `Official_Note_${employeeId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      }
    };

    window.html2pdf()
      .set(opt)
      .from(printContent)
      .save();
  };
  const [earnedLeave, setEarnedLeave] = useState(0);
  const [basicPayFromPension, setBasicPayFromPension] = useState(0);
  const [fetchingBalances, setFetchingBalances] = useState(true);

  React.useEffect(() => {
    const fetchBalancesAndPay = async () => {
      setFetchingBalances(true);
      try {
        const year = new Date().getFullYear();
        const [leaveRes, pensionRes] = await Promise.all([
          axios.get(`/v1/leave-sheet/employee/${employeeId}/earned-leave?year=${year}`),
          axios.get(`/v1/employee-master/${employeeId}/family-pension-report`).catch(err => {
            console.error("Family pension fetch failed", err);
            return { data: null };
          })
        ]);

        let elVal = 0;
        if (leaveRes.data && leaveRes.data.earned_leave && leaveRes.data.earned_leave.rows) {
          const rows = leaveRes.data.earned_leave.rows;
          let lastElRow = [...rows].reverse().find(r => r.balance_on_return !== null && r.balance_on_return !== undefined);
          if (lastElRow) {
            elVal = lastElRow.balance_on_return;
          }
        }
        setEarnedLeave(elVal);

        if (pensionRes.data && pensionRes.data.pay) {
          setBasicPayFromPension(pensionRes.data.pay);
        }
      } catch (err) {
        console.error("Failed to load dynamic data for official note report", err);
      } finally {
        setFetchingBalances(false);
      }
    };
    fetchBalancesAndPay();
  }, [employeeId]);

  if (loading) return null;

  const personal = profile?.personal || {};
  const employment = profile?.employment || {};

  const name = personal.full_name || `${personal.first_name || ''} ${personal.last_name || ''}`.trim();
  const designation = employment.designation || '';

  const nameMr = profile?.personal?.name_mr || '';
  const designationMr = profile?.employment?.designation_mr || '';

  let retirementDate = null;
  if (employment.date_of_retirement) {
    retirementDate = new Date(employment.date_of_retirement);
  } else if (personal.date_of_birth) {
    retirementDate = new Date(personal.date_of_birth);
    retirementDate.setFullYear(retirementDate.getFullYear() + 60);
  }

  const formatDateDot = (d) => {
    if (!d || isNaN(d.getTime())) return '30-06-2026';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  };

  // Basic pay fetched dynamically from third field (pay) of family pension report, with fallback to master
  const basicPayRaw = basicPayFromPension || profile?.employment?.basic_pay || profile?.payroll?.basic_salary || profile?.basic_pay || 0;

  // Try pensionReport first, then override, then 60
  const daPercentage = pensionReport?.da_rate ? Number(pensionReport.da_rate) : (profile?.pensioner_profile?.da_percentage_override ? Number(profile.pensioner_profile.da_percentage_override) : 60);

  const daAmount = Math.round(basicPayRaw * (daPercentage / 100));
  const totalSalary = basicPayRaw + daAmount;
  const leaveEncashmentAmount = Math.round((totalSalary * earnedLeave) / 30);

  const formatCurrency = (val) => Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-rose-50 text-rose-600 rounded-lg">
            <FileSignature size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Official Note</h3>
            <p className="text-sm text-gray-500">View and download the official note for leave encashment.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1000px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Official Note Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-white rounded-lg border border-gray-200 p-12 shadow-sm">
                <div ref={printRef} className="max-w-4xl mx-auto w-full text-black bg-white pt-4 pb-16 px-4 font-serif">

                  <div className="text-center font-bold text-[18px] mb-8">
                    कार्यालयीन टिप्पणी
                  </div>

                  <div className="text-[15px] leading-relaxed mb-6 flex">
                    <span className="w-16 flex-shrink-0">विषय :</span>
                    <span>
                      श्री./श्रीमती. {name}, {designation}  के अर्जित छुट्टी का भुगतान, निवृत्त
                      दि. {formatDateDot(retirementDate)}.
                    </span>
                  </div>

                  <div className="text-[15px] mb-4">
                    महोदय,
                  </div>

                  <div className="text-[15px] leading-relaxed text-justify mb-8 indent-12">
                    श्री./श्रीमती. {name}, {designation} दि. {formatDateDot(retirementDate)} को
                    निवृत्त हुए हैं। उनके अवकाश खाते का आरएओ (एमईएस) अधिकारियों द्वारा लेखा परीक्षण
                    किया गया है और उनके खाते मे {earnedLeave} दिन अर्जित छुट्टी शेष है। । अप्रयुक्त अर्जित अवकाश का
                    नकदीकरण, जिसके लिए गणना निम्नानुसार है : -
                  </div>

                  <div className="ml-24 mb-8 text-[15px] space-y-6">
                    <div className="flex items-center gap-4">
                      <span className="w-4 text-center">=</span>
                      <span>(मूल वेतन + डीए) शेष छुट्टी का नकदीकरण/30</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="w-4 text-center">=</span>
                      <div className="flex flex-col items-center">
                        <div className="border-b border-black text-center pb-1 w-full px-2">(Basic Pay +DA as on ) X Unutilized EL )</div>
                        <div className="text-center pt-1">30</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="w-4 text-center">=</span>
                      <div className="flex flex-col items-center">
                        <div className="border-b border-black text-center pb-1 w-full px-2">{basicPayRaw}+ {daPercentage}% DA {daAmount} X {earnedLeave}</div>
                        <div className="text-center pt-1">30</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="w-4 text-center">=</span>
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center">
                          <div className="border-b border-black text-center pb-1 w-full px-2">({totalSalary} x {earnedLeave})</div>
                          <div className="text-center pt-1">30</div>
                        </div>
                        <span>= Rs. {formatCurrency(leaveEncashmentAmount)}/-</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-8">
                      <span className="w-4 text-center">=</span>
                      <span>Rs. {formatCurrency(leaveEncashmentAmount)}/-</span>
                    </div>
                  </div>

                  <div className="text-[15px] leading-relaxed text-justify indent-12 mb-24">
                    यदि स्वीकृत हो, तो रु. {formatCurrency(leaveEncashmentAmount)}/- ( Rs. {numberToWords(leaveEncashmentAmount)} )
                    &nbsp;श्री./श्रीमती. {name}, {designation} को भुगतान किया जा
                    सकता है।
                  </div>

                  {/* Signatures */}
                  <div className="flex justify-between items-end mt-16 text-[15px] font-bold">
                    <div>
                      सीनियर लिपिक (पेंशन)
                    </div>
                    <div className="text-center">
                      <div className="mb-20">मुख्य लेखापाल</div>
                      <div> मुख्य कार्यकारी अधिकारी <br /> पुणे छावनी परिषद</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Employee360OfficialNoteCard;
